import { useEffect, useMemo, useRef, useState } from 'react';
import { iterateRows, normaliseHeader, splitLine } from '../reports/engine';
import { readPreview } from './MasterFileUploader';
import { downloadFile } from '../pages/Reports/ui';
import { startLoading } from '../utils/loader';
import { toast } from '../utils/toast';
import './BulkImportPanel.css';

const BATCH_SIZE = 1000;
const PREVIEW_LIMIT = 200;

const clean = (value) => String(value ?? '').replace(/[\t\r\n]+/g, ' ').trim();
const csvCell = (value) => {
  const text = String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};
const toCsv = (rows) => `﻿${rows.map((row) => row.map(csvCell).join(',')).join('\r\n')}\r\n`;
const fmt = (n) => Number(n || 0).toLocaleString();
const cap = (text) => text.charAt(0).toUpperCase() + text.slice(1);

/** Header row = first of the first rows that names a required column. */
function findHeader(rows, columns) {
  const names = new Set(
    columns.filter((c) => c.required).flatMap((c) => [c.key, c.label, ...(c.aliases || [])].map(normaliseHeader)),
  );
  for (let index = 0; index < Math.min(rows.length, 15); index += 1) {
    if (rows[index].some((cell) => names.has(normaliseHeader(cell)))) return index;
  }
  return 0;
}

function guessMapping(header, columns) {
  const cells = header.map((cell) => normaliseHeader(cell));
  const mapping = {};
  columns.forEach((column) => {
    const names = [column.label, column.key, ...(column.aliases || [])].map(normaliseHeader);
    let index = -1;
    for (const name of names) {
      index = cells.indexOf(name);
      if (index >= 0) break;
    }
    mapping[column.key] = index >= 0 ? String(index) : '';
  });
  return mapping;
}

/**
 * Text (CSV / tab / semicolon / pipe separated): the separator is picked from
 * the first 20 lines, so a title line above the header does not confuse it.
 */
function parseText(raw) {
  const text = String(raw || '').replace(/^﻿/, '');
  const lines = text.split(/\r?\n/).filter((line) => line.trim()).slice(0, 20);
  let best = '\t';
  let bestScore = 0;
  ['\t', ',', ';', '|'].forEach((delimiter) => {
    const score = lines.reduce((sum, line) => sum + (splitLine(line, delimiter).length > 1 ? 1 : 0), 0);
    if (score > bestScore) {
      best = delimiter;
      bestScore = score;
    }
  });
  return [...iterateRows(text, best)].map((row) => row.cells.map((cell) => String(cell ?? '')));
}

/** Cell text; MPIN / password columns (secret) are masked. */
const cellText = (column, value) => (column.secret && value ? '••••' : value);

const STATUS_LABEL = { created: 'Added', skipped: 'Skipped', error: 'Not added' };
const STEPS = ['Choose file', 'Match columns & check', 'Result'];

const Icon = ({ d, size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
);
const UPLOAD = <><path d="M12 16V4M7 9l5-5 5 5" /><path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" /></>;
const FILE = <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></>;
const DOWNLOAD = <><path d="M12 4v12M7 11l5 5 5-5" /><path d="M4 20h16" /></>;

/**
 * Bulk upload in a large modal: template download, Excel / CSV file (drag
 * and drop) or pasted rows, column picker (guessed from the header names), a
 * checked preview, then upload in batches with a per-row result report.
 * Outcomes are announced with toast messages.
 *
 * open / onClose / title: the modal
 * columns: [{ key, label, required, aliases, hint }]
 * check(record) -> '' or the reason the row cannot be added (client-side)
 * upload(records) -> { created, skipped, errors, results: [{ line, status, message }] }
 */
function BulkImportPanel({ open = true, onClose, title, noun, nounPlural, columns, sampleRows, templateName, intro, check, upload, onDone }) {
  const inputRef = useRef(null);
  const [source, setSource] = useState(null); // { name, sheets: [{ name, rows }] }
  const [sheetIndex, setSheetIndex] = useState(0);
  const [headerRow, setHeaderRow] = useState(0);
  const [mapping, setMapping] = useState({});
  const [pasted, setPasted] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [previewFilter, setPreviewFilter] = useState('all');
  const [mappingOpen, setMappingOpen] = useState(false);
  const [dragging, setDragging] = useState(false);

  const close = () => {
    if (busy) return;
    onClose?.();
  };

  // Esc closes; the page behind does not scroll while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => event.key === 'Escape' && !busy && onClose?.();
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [open, busy, onClose]);

  const sheetRows = useMemo(() => source?.sheets[sheetIndex]?.rows || [], [source, sheetIndex]);

  const load = (name, sheets) => {
    const first = Math.max(0, sheets.findIndex((sheet) => sheet.rows.length > 1));
    const rows = sheets[first]?.rows || [];
    if (rows.length < 2) throw new Error(`The file needs a header row and at least one ${noun}.`);
    const header = findHeader(rows, columns);
    const guess = guessMapping(rows[header], columns);
    setSource({ name, sheets });
    setSheetIndex(first);
    setHeaderRow(header);
    setMapping(guess);
    setReport(null);
    setPreviewFilter('all');
    const unmatched = columns.filter((c) => c.required && guess[c.key] === '');
    setMappingOpen(unmatched.length > 0);
  };

  const readFile = async (file) => {
    if (!file) return;
    const task = startLoading('Reading file…', { detail: file.name });
    try {
      if (!/\.(xlsx|xlsb|xls)$/i.test(file.name)) {
        load(file.name, [{ name: file.name, rows: parseText(await file.text()) }]);
        return;
      }
      const preview = await readPreview(file);
      // The preview holds the first rows only; read every sheet in full.
      const sheets = await Promise.all(
        preview.sheets.map(async (sheet, index) => ({
          name: sheet.name,
          rows: (await preview.readAll(index)).map((row) => row.map((cell) => String(cell ?? ''))),
        })),
      );
      load(file.name, sheets);
    } catch (err) {
      toast.error('Could not read the file', err.message);
    } finally {
      task.done();
    }
  };

  const chooseFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    readFile(file);
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    readFile(event.dataTransfer.files?.[0]);
  };

  const usePasted = () => {
    try {
      load('Pasted rows', [{ name: 'Pasted', rows: parseText(pasted) }]);
    } catch (err) {
      toast.error('Could not use the pasted rows', err.message);
    }
  };

  const downloadTemplate = () => {
    const rows = [columns.map((c) => c.label), ...(sampleRows || []).map((row) => columns.map((c) => row[c.key] ?? ''))];
    downloadFile(templateName, toCsv(rows), 'text/csv;charset=utf-8');
    toast.info('Template downloaded', `${templateName} — fill it in and drop it here.`);
  };

  const width = sheetRows.reduce((max, row) => Math.max(max, row.length), 0);
  const header = sheetRows[headerRow] || [];
  const missing = columns.filter((c) => c.required && mapping[c.key] === '');
  const matched = columns.filter((c) => mapping[c.key] !== '' && mapping[c.key] !== undefined).length;

  const items = useMemo(() => {
    if (!source) return [];
    const list = [];
    sheetRows.slice(headerRow + 1).forEach((cells, offset) => {
      if (!cells.some((cell) => clean(cell))) return;
      const record = { line: headerRow + offset + 2 };
      columns.forEach((c) => {
        record[c.key] = mapping[c.key] === '' || mapping[c.key] === undefined ? '' : clean(cells[Number(mapping[c.key])]);
      });
      list.push({ record, problem: check ? check(record) : '' });
    });
    return list;
  }, [source, sheetRows, headerRow, mapping, columns, check]);

  const ready = items.filter((item) => !item.problem);
  const problems = items.filter((item) => item.problem);
  const previewItems = previewFilter === 'problems' ? problems : previewFilter === 'ready' ? ready : items;

  const submit = async () => {
    if (!ready.length) return;
    setBusy(true);
    const parts = Math.ceil(ready.length / BATCH_SIZE);
    const task = startLoading(`Uploading ${nounPlural}…`, { detail: `${fmt(ready.length)} rows` });
    const all = problems.map((item) => ({ ...item.record, status: 'error', message: item.problem }));
    const total = { created: 0, skipped: 0, errors: problems.length };
    try {
      for (let index = 0; index < parts; index += 1) {
        if (parts > 1) task.update(null, `Part ${index + 1} of ${parts} · ${fmt(ready.length)} rows`);
        const batch = ready.slice(index * BATCH_SIZE, (index + 1) * BATCH_SIZE).map((item) => item.record);
        // eslint-disable-next-line no-await-in-loop
        const result = await upload(batch);
        total.created += result.created || 0;
        total.skipped += result.skipped || 0;
        total.errors += result.errors || 0;
        const byLine = new Map(batch.map((record) => [record.line, record]));
        (result.results || []).forEach((row) => all.push({ ...byLine.get(row.line), ...row }));
      }
      all.sort((a, b) => a.line - b.line);
      setReport({ ...total, rows: all, source: source.name });
      setOnlyProblems(total.skipped + total.errors > 0);
      const detail = [
        `${fmt(total.created)} added`,
        total.skipped ? `${fmt(total.skipped)} skipped` : '',
        total.errors ? `${fmt(total.errors)} not added` : '',
      ]
        .filter(Boolean)
        .join(' · ');
      if (total.created && !total.skipped && !total.errors) toast.success(`${cap(nounPlural)} uploaded`, detail);
      else if (total.created) toast.warn(`${cap(nounPlural)} uploaded with some rows left out`, `${detail}. See the result for the reasons.`);
      else toast.error(`No ${nounPlural} were added`, `${detail}. See the result for the reasons.`);
      if (total.created) onDone?.();
    } catch (err) {
      toast.error(
        'Upload stopped',
        `${err.message}${total.created ? ` — ${fmt(total.created)} ${nounPlural} were added before it stopped.` : ''}`,
      );
      if (total.created) onDone?.();
    } finally {
      task.done();
      setBusy(false);
    }
  };

  const reset = () => {
    setSource(null);
    setReport(null);
    setPasted('');
  };

  const downloadReport = () => {
    const rows = [
      ['Row', ...columns.map((c) => c.label), 'Result', 'Reason'],
      ...report.rows.map((row) => [row.line, ...columns.map((c) => cellText(c, row[c.key] ?? '')), STATUS_LABEL[row.status] || row.status, row.message || '']),
    ];
    downloadFile(`${nounPlural} upload result.csv`, toCsv(rows), 'text/csv;charset=utf-8');
    toast.info('Result downloaded', `${nounPlural} upload result.csv`);
  };

  if (!open) return null;

  const step = report ? 2 : source ? 1 : 0;
  const shownReport = report ? (onlyProblems ? report.rows.filter((row) => row.status !== 'created') : report.rows) : [];

  return (
    <div className="bim" role="dialog" aria-modal="true" aria-labelledby="bim-title">
      <div className="bim__backdrop" onClick={close} />
      <div className="bim__panel">
        <header className="bim__head">
          <span className="bim__badge">
            <Icon d={UPLOAD} size={22} />
          </span>
          <div className="bim__titles">
            <h2 id="bim-title">{title || `Bulk upload ${nounPlural}`}</h2>
            {intro ? <p>{intro}</p> : null}
          </div>
          <button type="button" className="bim__close" onClick={close} disabled={busy} aria-label="Close">
            ×
          </button>
        </header>

        <ol className="bim__steps" aria-label="Steps">
          {STEPS.map((label, index) => (
            <li key={label} className={index === step ? 'is-current' : index < step ? 'is-done' : ''}>
              <span>{index < step ? '✓' : index + 1}</span>
              {label}
            </li>
          ))}
        </ol>

        <div className="bim__body">
          {/* ---- Step 1: choose file ---------------------------------------- */}
          {step === 0 ? (
            <div className="bim__start">
              <div className="bim__source">
                <div
                  className={`bim__drop${dragging ? ' is-over' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => inputRef.current?.click()}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={onDrop}
                >
                  <span className="bim__drop-icon">
                    <Icon d={UPLOAD} size={28} />
                  </span>
                  <strong>Drop your Excel or CSV file here</strong>
                  <span>
                    or <u>browse</u> · .xlsx, .xlsb, .csv, .txt
                  </span>
                </div>
                <input ref={inputRef} type="file" accept=".xlsx,.xlsb,.csv,.txt,.tsv" hidden onChange={chooseFile} aria-label="File" />

                <button type="button" className="bim__paste-toggle" onClick={() => setPasteOpen((v) => !v)} aria-expanded={pasteOpen}>
                  {pasteOpen ? '▾' : '▸'} Or paste rows copied from Excel
                </button>
                {pasteOpen ? (
                  <div className="bim__paste">
                    <textarea
                      rows={6}
                      value={pasted}
                      onChange={(event) => setPasted(event.target.value)}
                      placeholder={columns.map((column) => column.label).join('\t')}
                      aria-label="Pasted rows"
                    />
                    <button type="button" className="bim-btn bim-btn--ghost" onClick={usePasted} disabled={!pasted.trim()}>
                      Check pasted rows
                    </button>
                  </div>
                ) : null}
              </div>

              <aside className="bim__columns">
                <div className="bim__columns-head">
                  <strong>Columns in the file</strong>
                  <span>{columns.filter((c) => c.required).length} required · header names can differ</span>
                </div>
                <ul>
                  {columns.map((column) => (
                    <li key={column.key}>
                      <span className="bim__col-name">
                        {column.label}
                        {column.required ? <em>Required</em> : null}
                      </span>
                      {column.hint ? <small>{column.hint}</small> : null}
                    </li>
                  ))}
                </ul>
                <button type="button" className="bim-btn bim-btn--soft" onClick={downloadTemplate}>
                  <Icon d={DOWNLOAD} size={16} /> Download template (CSV)
                </button>
              </aside>
            </div>
          ) : null}

          {/* ---- Step 2: map + preview ------------------------------------------ */}
          {step === 1 ? (
            <div className="bim__check">
              <div className="bim__file">
                <span className="bim__file-icon">
                  <Icon d={FILE} size={18} />
                </span>
                <div>
                  <strong>{source.name}</strong>
                  <span>
                    {fmt(items.length)} rows · {matched} of {columns.length} columns matched
                  </span>
                </div>
                <button type="button" className="bim-link" onClick={reset} disabled={busy}>
                  Change file
                </button>
              </div>

              <section className={`bim__mapping${mappingOpen ? ' is-open' : ''}`}>
                <button type="button" className="bim__mapping-head" onClick={() => setMappingOpen((v) => !v)} aria-expanded={mappingOpen}>
                  <span>{mappingOpen ? '▾' : '▸'} Column matching</span>
                  <small>
                    {missing.length
                      ? `Choose: ${missing.map((c) => c.label).join(', ')}`
                      : `${matched} matched from the header names — open to change`}
                  </small>
                </button>
                {mappingOpen ? (
                  <div className="bim__mapping-grid">
                    {source.sheets.length > 1 ? (
                      <label>
                        Sheet
                        <select
                          value={sheetIndex}
                          onChange={(event) => {
                            const index = Number(event.target.value);
                            const rows = source.sheets[index].rows;
                            const head = findHeader(rows, columns);
                            setSheetIndex(index);
                            setHeaderRow(head);
                            setMapping(guessMapping(rows[head] || [], columns));
                          }}
                        >
                          {source.sheets.map((sheet, index) => (
                            <option key={sheet.name} value={index}>
                              {sheet.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                    <label>
                      Header row
                      <select
                        value={headerRow}
                        onChange={(event) => {
                          const index = Number(event.target.value);
                          setHeaderRow(index);
                          setMapping(guessMapping(sheetRows[index] || [], columns));
                        }}
                      >
                        {sheetRows.slice(0, 15).map((row, index) => (
                          <option key={index} value={index}>
                            Row {index + 1}: {row.slice(0, 4).map((cell) => clean(cell) || '—').join(' | ')}
                          </option>
                        ))}
                      </select>
                    </label>
                    {columns.map((column) => (
                      <label key={column.key} className={mapping[column.key] === '' ? (column.required ? 'is-missing' : '') : 'is-set'}>
                        {column.label}
                        {column.required ? ' *' : ''}
                        <select
                          aria-label={`${column.label} column`}
                          value={mapping[column.key] ?? ''}
                          onChange={(event) => setMapping((prev) => ({ ...prev, [column.key]: event.target.value }))}
                        >
                          <option value="">{column.required ? 'Choose column…' : 'Not in this file'}</option>
                          {Array.from({ length: width }, (_, index) => {
                            const sample = sheetRows.slice(headerRow + 1).find((row) => clean(row[index]));
                            return (
                              <option key={index} value={String(index)}>
                                {index + 1}: {clean(header[index]) || `Column ${index + 1}`}
                                {sample ? ` (e.g. ${clean(sample[index]).slice(0, 24)})` : ''}
                              </option>
                            );
                          })}
                        </select>
                      </label>
                    ))}
                  </div>
                ) : null}
              </section>

              {missing.length ? (
                <p className="bim__missing">Choose the column for {missing.map((c) => c.label).join(', ')} to see the check.</p>
              ) : (
                <>
                  <div className="bim__summary">
                    <div className="bim-stat bim-stat--good">
                      <strong>{fmt(ready.length)}</strong>
                      <span>ready to upload</span>
                    </div>
                    <div className={`bim-stat ${problems.length ? 'bim-stat--bad' : 'bim-stat--muted'}`}>
                      <strong>{fmt(problems.length)}</strong>
                      <span>with problems (left out)</span>
                    </div>
                    <p>{cap(nounPlural)} that already exist are skipped, not changed.</p>
                  </div>

                  <div className="bim__filters" role="group" aria-label="Show rows">
                    {[
                      ['all', `All (${fmt(items.length)})`],
                      ['ready', `Ready (${fmt(ready.length)})`],
                      ['problems', `Problems (${fmt(problems.length)})`],
                    ].map(([id, label]) => (
                      <button key={id} type="button" aria-pressed={previewFilter === id} onClick={() => setPreviewFilter(id)}>
                        {label}
                      </button>
                    ))}
                  </div>

                  <div className="bim__table">
                    <table>
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Check</th>
                          {columns.map((c) => (
                            <th key={c.key}>{c.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewItems.slice(0, PREVIEW_LIMIT).map((item) => (
                          <tr key={item.record.line} className={item.problem ? 'is-bad' : ''}>
                            <td className="bim__line">{item.record.line}</td>
                            <td>
                              {item.problem ? (
                                <span className="bim-pill bim-pill--bad" title={item.problem}>
                                  {item.problem}
                                </span>
                              ) : (
                                <span className="bim-pill bim-pill--good">OK</span>
                              )}
                            </td>
                            {columns.map((c) => (
                              <td key={c.key}>{cellText(c, item.record[c.key]) || <span className="bim__dash">—</span>}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {!previewItems.length ? <p className="bim__empty">Nothing to show.</p> : null}
                  </div>
                  {previewItems.length > PREVIEW_LIMIT ? (
                    <p className="bim__note">
                      Showing the first {PREVIEW_LIMIT} of {fmt(previewItems.length)} rows — all of them are uploaded.
                    </p>
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          {/* ---- Step 3: result ------------------------------------------------------ */}
          {step === 2 ? (
            <div className="bim__check">
              <div className="bim__summary bim__summary--result">
                <div className="bim-stat bim-stat--good">
                  <strong>{fmt(report.created)}</strong>
                  <span>added</span>
                </div>
                <div className={`bim-stat ${report.skipped ? 'bim-stat--warn' : 'bim-stat--muted'}`}>
                  <strong>{fmt(report.skipped)}</strong>
                  <span>skipped (already there)</span>
                </div>
                <div className={`bim-stat ${report.errors ? 'bim-stat--bad' : 'bim-stat--muted'}`}>
                  <strong>{fmt(report.errors)}</strong>
                  <span>not added</span>
                </div>
              </div>

              <div className="bim__filters" role="group" aria-label="Show rows">
                <button type="button" aria-pressed={!onlyProblems} onClick={() => setOnlyProblems(false)}>
                  All rows ({fmt(report.rows.length)})
                </button>
                <button type="button" aria-pressed={onlyProblems} onClick={() => setOnlyProblems(true)}>
                  Skipped / not added ({fmt(report.skipped + report.errors)})
                </button>
              </div>

              <div className="bim__table">
                <table>
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Result</th>
                      {columns.filter((c) => c.showInReport !== false).map((c) => (
                        <th key={c.key}>{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {shownReport.slice(0, 500).map((row) => (
                      <tr key={row.line} className={row.status === 'created' ? '' : row.status === 'skipped' ? 'is-skip' : 'is-bad'}>
                        <td className="bim__line">{row.line}</td>
                        <td>
                          <span className={`bim-pill bim-pill--${row.status === 'created' ? 'good' : row.status === 'skipped' ? 'warn' : 'bad'}`}>
                            {STATUS_LABEL[row.status] || row.status}
                          </span>
                          {row.message ? <small className="bim__reason">{row.message}</small> : null}
                        </td>
                        {columns.filter((c) => c.showInReport !== false).map((c) => (
                          <td key={c.key}>{cellText(c, row[c.key]) || <span className="bim__dash">—</span>}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!shownReport.length ? <p className="bim__empty">Nothing to show — every row was added.</p> : null}
              </div>
              {shownReport.length > 500 ? <p className="bim__note">Showing the first 500 rows; the CSV has all of them.</p> : null}
            </div>
          ) : null}
        </div>

        <footer className="bim__foot">
          {step === 0 ? (
            <>
              <span className="bim__foot-note">Nothing is saved until you press Upload.</span>
              <button type="button" className="bim-btn bim-btn--ghost" onClick={close}>
                Cancel
              </button>
            </>
          ) : null}
          {step === 1 ? (
            <>
              <button type="button" className="bim-btn bim-btn--ghost" onClick={reset} disabled={busy}>
                ← Back
              </button>
              <span className="bim__foot-note">
                {problems.length && !missing.length ? `${fmt(problems.length)} row${problems.length === 1 ? '' : 's'} with problems will be left out.` : ''}
              </span>
              <button type="button" className="bim-btn bim-btn--primary" onClick={submit} disabled={busy || !ready.length || missing.length > 0}>
                {busy ? 'Uploading…' : `Upload ${fmt(ready.length)} ${ready.length === 1 ? noun : nounPlural}`}
              </button>
            </>
          ) : null}
          {step === 2 ? (
            <>
              <button type="button" className="bim-btn bim-btn--ghost" onClick={downloadReport}>
                <Icon d={DOWNLOAD} size={16} /> Download result (CSV)
              </button>
              <span className="bim__foot-note" />
              <button type="button" className="bim-btn bim-btn--ghost" onClick={reset}>
                Upload another file
              </button>
              <button type="button" className="bim-btn bim-btn--primary" onClick={close}>
                Done
              </button>
            </>
          ) : null}
        </footer>
      </div>
    </div>
  );
}

export default BulkImportPanel;
